import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

/**
 * El paginador del listado en desktop.
 *
 * <h3>Una ventana de páginas, no todas</h3>
 * El paginador anterior dibujaba un botón por cada página que existiera: con el catálogo entero son
 * treinta y pico de números en dos renglones, donde el que está activo se pierde y no hay forma de
 * llegar al final sin contar. Acá se ven siempre siete casilleros como mucho —primera, última, la
 * actual y dos de cada lado— y el resto se resume en puntos suspensivos.
 *
 * <h3>La ventana tiene ancho fijo</h3>
 * `desde` se clampea contra `total - tamanio` en vez de centrarse siempre en la actual. Si se
 * centrara, en la página 1 la ventana arrancaría en -1 y quedarían cuatro casilleros; en la
 * anteúltima, cinco. El paginador cambiaría de ancho al navegarlo y los botones se correrían
 * debajo del dedo/mouse justo cuando se los está usando.
 *
 * <h3>Por qué "13–24 de 348" y no sólo los números</h3>
 * Es la única parte que dice cuántos resultados hay. Sin eso, "página 2 de 29" no responde la
 * pregunta que se hace quien filtró algo, que es si vale la pena seguir filtrando.
 *
 * @param page          página actual, base 0 (como la devuelve Spring).
 * @param totalPages    cuántas páginas hay.
 * @param totalElements cuántos resultados hay en total. Opcional: sin esto no se muestra el conteo.
 * @param pageSize      tamaño de página, para calcular el rango que se está viendo.
 * @param onChange      recibe la página nueva, base 0.
 */

/** Cuántos números se ven a cada lado del actual, y el ancho fijo de la ventana. */
const ALREDEDOR = 2;
const ANCHO_VENTANA = ALREDEDOR * 2 + 1;

/**
 * Los casilleros a dibujar: números de página y huecos.
 *
 * <p>Devuelve la ventana más la primera y la última, con un hueco donde el salto es de más de una
 * página. El hueco no se pone si el salto es exactamente de una: ahí ocupa lo mismo que el número
 * que está tapando, y esconder la página 2 detrás de "…" para ahorrar nada es peor.
 */
const casilleros = (actual, total) => {
    const tamanio = Math.min(ANCHO_VENTANA, total);
    const desde = Math.max(0, Math.min(actual - ALREDEDOR, total - tamanio));

    const numeros = new Set();
    numeros.add(0);
    numeros.add(total - 1);
    for (let i = desde; i < desde + tamanio; i++) numeros.add(i);

    const ordenados = [...numeros].filter((n) => n >= 0 && n < total).sort((a, b) => a - b);

    const salida = [];
    ordenados.forEach((n, i) => {
        if (i > 0 && n - ordenados[i - 1] > 1) salida.push({ hueco: true, clave: `hueco-${n}` });
        salida.push({ pagina: n, clave: `pag-${n}` });
    });

    return salida;
};

const Paginador = ({ page, totalPages, totalElements, pageSize, onChange }) => {
    const total = Number(totalPages) || 0;
    const actual = Number(page) || 0;

    if (total <= 0) return null;

    const irA = (n) => {
        if (n < 0 || n > total - 1 || n === actual) return;
        onChange(n);
    };

    const desdeItem = actual * (pageSize || 0) + 1;
    const hastaItem = totalElements ? Math.min(desdeItem + (pageSize || 0) - 1, totalElements) : 0;

    return (
        <nav className="paginador" aria-label="Paginación de resultados">
            {!!totalElements && (
                <p className="paginador__conteo">
                    <strong>
                        {desdeItem}–{hastaItem}
                    </strong>{" "}
                    de {totalElements} {totalElements === 1 ? "publicación" : "publicaciones"}
                </p>
            )}

            {total > 1 && (
                <ul className="paginador__paginas">
                    <li>
                        <button
                            type="button"
                            className="paginador__boton paginador__boton--salto"
                            onClick={() => irA(0)}
                            disabled={actual === 0}
                            aria-label="Primera página"
                            title="Primera página"
                        >
                            <FontAwesomeIcon icon="fa-solid fa-angles-left" />
                        </button>
                    </li>
                    <li>
                        <button
                            type="button"
                            className="paginador__boton paginador__boton--salto"
                            onClick={() => irA(actual - 1)}
                            disabled={actual === 0}
                            aria-label="Página anterior"
                            title="Página anterior"
                        >
                            <FontAwesomeIcon icon="fa-solid fa-angle-left" />
                        </button>
                    </li>

                    {casilleros(actual, total).map((c) =>
                        c.hueco ? (
                            <li key={c.clave} className="paginador__hueco" aria-hidden="true">
                                …
                            </li>
                        ) : (
                            <li key={c.clave}>
                                <button
                                    type="button"
                                    className={
                                        c.pagina === actual
                                            ? "paginador__boton paginador__boton--activo"
                                            : "paginador__boton"
                                    }
                                    onClick={() => irA(c.pagina)}
                                    aria-current={c.pagina === actual ? "page" : undefined}
                                    aria-label={`Página ${c.pagina + 1}`}
                                >
                                    {c.pagina + 1}
                                </button>
                            </li>
                        )
                    )}

                    <li>
                        <button
                            type="button"
                            className="paginador__boton paginador__boton--salto"
                            onClick={() => irA(actual + 1)}
                            disabled={actual >= total - 1}
                            aria-label="Página siguiente"
                            title="Página siguiente"
                        >
                            <FontAwesomeIcon icon="fa-solid fa-angle-right" />
                        </button>
                    </li>
                    <li>
                        <button
                            type="button"
                            className="paginador__boton paginador__boton--salto"
                            onClick={() => irA(total - 1)}
                            disabled={actual >= total - 1}
                            aria-label="Última página"
                            title="Última página"
                        >
                            <FontAwesomeIcon icon="fa-solid fa-angles-right" />
                        </button>
                    </li>
                </ul>
            )}
        </nav>
    );
};

export { Paginador };
export default Paginador;
